import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Undo2, Redo2, ZoomIn, ZoomOut, Maximize2, Magnet, Code2, Eye, Save, Download, Upload, Star } from "lucide-react";
import { useTemplateState } from "./use-template-state";
import Canvas from "./canvas";
import LeftPanel from "./left-panel";
import PropertiesPanel from "./properties-panel";
import PreviewModal from "./preview-modal";
import { IconBtn, PrimaryBtn, SecondaryBtn } from "./ui";
import { usePreviewData, describeSource } from "./data-source";
import { compileTemplateHtml, ZP } from "../compile";
import { cloneWithNewIds, uid } from "../schema";
import { downloadFormats, readFormatFiles } from "../transfer";
import { getDocType } from "../doc-types";
import { saveFormat } from "../api";
import successToast from "../../../../helper-components/success-toast";
import errorToast from "../../../../helper-components/error-toast";

const EMPTY_SEL = { bandId: null, ids: [], colId: null };

function isTyping(e) {
  const t = e.target;
  return t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);
}

export default function Editor({ initialTemplate, formatId: initialId, isDefault, onBack }) {
  const state = useTemplateState(initialTemplate);
  const { template, update, undo, redo } = state;
  const [selection, setSelection] = useState(EMPTY_SEL);
  const [zoom, setZoom] = useState(1);
  const [snap, setSnap] = useState(true);
  const [showFormulas, setShowFormulas] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formatId, setFormatId] = useState(initialId || null);
  const clipboard = useRef(null);
  const canvasHost = useRef(null);
  const fileInput = useRef(null);
  const previewData = usePreviewData(template.docType);
  const sample = previewData.sample;
  const dt = getDocType(template.docType);
  const ps = ZP.paperSize(template);

  const fit = useCallback(() => {
    const host = canvasHost.current;
    if (!host) return;
    const avail = host.clientWidth - 60;
    const z = avail / ((ps.width + 40) * ZP.MM_TO_PX);
    setZoom(Math.max(0.3, Math.min(2.5, Math.round(z * 20) / 20)));
  }, [ps.width]);

  useEffect(() => {
    fit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ps.width]);

  // drop selection that no longer exists (after undo / delete)
  useEffect(() => {
    if (!selection.bandId) return;
    const band = template.bands.find((b) => b.id === selection.bandId);
    if (!band) return setSelection(EMPTY_SEL);
    const ids = selection.ids.filter((id) => (band.elements || []).some((e) => e.id === id));
    if (ids.length !== selection.ids.length) setSelection({ ...selection, ids });
  }, [template, selection]);

  // warn before leaving with unsaved changes
  useEffect(() => {
    const h = (e) => {
      if (state.isDirty()) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [state]);

  const selectedEls = useCallback(() => {
    const band = template.bands.find((b) => b.id === selection.bandId);
    return band ? { band, els: (band.elements || []).filter((e) => selection.ids.includes(e.id)) } : { band: null, els: [] };
  }, [template, selection]);

  const nudge = (dx, dy) => {
    const { band, els } = selectedEls();
    if (!band || !els.length || band.type === "table") return;
    update(
      (t) => {
        const b = t.bands.find((q) => q.id === band.id);
        b.elements.forEach((e) => {
          if (selection.ids.includes(e.id)) {
            e.x = Math.round((e.x + dx) * 100) / 100;
            e.y = Math.round((e.y + dy) * 100) / 100;
          }
        });
      },
      { key: "nudge" }
    );
  };

  const deleteSelection = () => {
    const { band, els } = selectedEls();
    if (!band) return;
    if (els.length) {
      if (band.type === "table") return; // the table of a table section is removed with its section
      update((t) => {
        const b = t.bands.find((q) => q.id === band.id);
        b.elements = b.elements.filter((e) => !selection.ids.includes(e.id));
      });
      setSelection({ ...selection, ids: [] });
    }
  };

  const copy = () => {
    const { band, els } = selectedEls();
    if (band && els.length && band.type === "fixed") clipboard.current = JSON.parse(JSON.stringify(els));
  };

  const paste = (offset = 3) => {
    const items = clipboard.current;
    if (!items || !items.length) return;
    const target = template.bands.find((b) => b.id === selection.bandId && b.type === "fixed") || template.bands.find((b) => b.type === "fixed");
    if (!target) return;
    const copies = items.map((e) => {
      const c = cloneWithNewIds(e);
      c.id = uid(e.type.slice(0, 2));
      c.x = Math.round((c.x + offset) * 100) / 100;
      c.y = Math.round((c.y + offset) * 100) / 100;
      return c;
    });
    update((t) => t.bands.find((b) => b.id === target.id).elements.push(...copies));
    setSelection({ bandId: target.id, ids: copies.map((c) => c.id), colId: null });
    clipboard.current = copies;
  };

  useEffect(() => {
    const onKey = (e) => {
      if (preview || isTyping(e) || editingId) return;
      const mod = e.ctrlKey || e.metaKey;
      const k = e.key.toLowerCase();
      if (mod && k === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (mod && (k === "y" || (k === "z" && e.shiftKey))) {
        e.preventDefault();
        redo();
      } else if (mod && k === "c") {
        copy();
      } else if (mod && k === "x") {
        copy();
        deleteSelection();
      } else if (mod && k === "v") {
        e.preventDefault();
        paste();
      } else if (mod && k === "d") {
        e.preventDefault();
        copy();
        paste(2);
      } else if (mod && k === "s") {
        e.preventDefault();
        save(false);
      } else if (e.key === "Delete" || e.key === "Backspace") {
        if (selection.ids.length) {
          e.preventDefault();
          deleteSelection();
        }
      } else if (e.key === "Escape") {
        setSelection(EMPTY_SEL);
      } else if (e.key.startsWith("Arrow") && selection.ids.length) {
        e.preventDefault();
        const step = e.shiftKey ? 5 : snap ? 0.5 : 0.1;
        nudge(e.key === "ArrowLeft" ? -step : e.key === "ArrowRight" ? step : 0, e.key === "ArrowUp" ? -step : e.key === "ArrowDown" ? step : 0);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const save = async (setDefault) => {
    const t = state.getTemplate();
    if (!t.name || !t.name.trim()) {
      errorToast("Please give the format a name");
      return;
    }
    const out = ZP.renderDocument(t, sample);
    if (out.errors.length && !window.confirm(`This format has ${out.errors.length} formula error(s), e.g.\n\n${out.errors[0]}\n\nSave anyway?`)) return;
    setSaving(true);
    try {
      const res = await saveFormat({
        id: formatId || undefined,
        type: t.docType,
        name: t.name.trim(),
        html: compileTemplateHtml(t),
        template_json: t,
        paper: t.paper?.preset || "A4",
        set_default: !!setDefault,
      });
      setFormatId(res.id);
      state.markSaved();
      successToast(setDefault ? `Saved. ${dt.label} will now print with this format.` : "Format saved");
    } catch (e) {
      errorToast(e?.message || "Could not save the format");
    } finally {
      setSaving(false);
    }
  };

  const exportJson = () => downloadFormats([state.getTemplate()], template.name || "format");

  // Replaces the design on the canvas (undoable); the name and document type are kept.
  const importJson = async (file) => {
    if (!file) return;
    try {
      const [t] = await readFormatFiles([file], template.docType);
      if (!window.confirm(`Replace the current design with "${t.name}"? You can undo this.`)) return;
      update(() => ({ ...t, name: template.name, docType: template.docType }));
      setSelection(EMPTY_SEL);
      successToast("Design imported");
    } catch (e) {
      errorToast(e.message);
    }
  };

  const back = () => {
    if (state.isDirty() && !window.confirm("You have unsaved changes. Leave without saving?")) return;
    onBack(formatId);
  };

  return (
    <div className="flex flex-col h-screen bg-white">
      <div className="h-12 shrink-0 border-b border-EAECF0 flex items-center gap-1 px-2">
        <IconBtn title="Back to formats" onClick={back}>
          <ArrowLeft size={16} />
        </IconBtn>
        <input
          className="border border-white hover:border-EAECF0 focus:border-EAECF0 rounded px-2 py-1 text-sm font-semibold text-000000 w-64 focus:outline-none"
          value={template.name}
          onChange={(e) => update((t) => (t.name = e.target.value), { key: "doc-name" })}
          placeholder="Format name"
        />
        <span className="text-[11px] text-667085 whitespace-nowrap">
          {dt.label} · {template.paper?.preset === "CUSTOM" ? `${ps.width}×${ps.continuous ? "roll" : ps.height} mm` : template.paper?.preset}
          {state.isDirty() ? " · unsaved" : ""}
          {isDefault ? " · used for printing" : ""}
        </span>
        <div className="w-px h-6 bg-EAECF0 mx-2" />
        <IconBtn title="Undo (Ctrl+Z)" onClick={undo} disabled={!state.canUndo}>
          <Undo2 size={16} />
        </IconBtn>
        <IconBtn title="Redo (Ctrl+Y)" onClick={redo} disabled={!state.canRedo}>
          <Redo2 size={16} />
        </IconBtn>
        <div className="w-px h-6 bg-EAECF0 mx-2" />
        <IconBtn title="Zoom out" onClick={() => setZoom((z) => Math.max(0.25, Math.round((z - 0.1) * 100) / 100))}>
          <ZoomOut size={16} />
        </IconBtn>
        <span className="text-[11px] text-344054 w-10 text-center">{Math.round(zoom * 100)}%</span>
        <IconBtn title="Zoom in" onClick={() => setZoom((z) => Math.min(3, Math.round((z + 0.1) * 100) / 100))}>
          <ZoomIn size={16} />
        </IconBtn>
        <IconBtn title="Fit to screen" onClick={fit}>
          <Maximize2 size={15} />
        </IconBtn>
        <div className="w-px h-6 bg-EAECF0 mx-2" />
        <IconBtn title="Snap to grid and edges" active={snap} onClick={() => setSnap(!snap)}>
          <Magnet size={15} />
        </IconBtn>
        <IconBtn title="Show formulas instead of sample values" active={showFormulas} onClick={() => setShowFormulas(!showFormulas)}>
          <Code2 size={15} />
        </IconBtn>
        <div className="flex-1" />
        <IconBtn title="Import a design from a .json file" onClick={() => fileInput.current && fileInput.current.click()}>
          <Upload size={15} /> Import
        </IconBtn>
        <input
          ref={fileInput}
          type="file"
          accept="application/json,.json"
          className="hidden"
          onChange={(e) => {
            importJson(e.target.files[0]);
            e.target.value = "";
          }}
        />
        <IconBtn title="Export this design as a .json file" onClick={exportJson}>
          <Download size={15} /> Export
        </IconBtn>
        <SecondaryBtn onClick={() => setPreview(true)} className="ml-1 !py-1.5">
          <Eye size={14} /> Preview
        </SecondaryBtn>
        <SecondaryBtn onClick={() => save(false)} disabled={saving} className="!py-1.5">
          <Save size={14} /> Save
        </SecondaryBtn>
        <PrimaryBtn onClick={() => save(true)} loading={saving} className="!py-1.5">
          <Star size={14} /> Save & use for printing
        </PrimaryBtn>
      </div>
      <div className="flex flex-1 min-h-0">
        <LeftPanel template={template} sample={sample} selection={selection} setSelection={setSelection} update={update} contentWidth={ps.contentWidth} />
        <div ref={canvasHost} className="flex-1 min-w-0 flex">
          <Canvas
            template={template}
            sample={sample}
            zoom={zoom}
            selection={selection}
            setSelection={setSelection}
            state={state}
            showFormulas={showFormulas}
            snap={snap}
            editingId={editingId}
            setEditingId={setEditingId}
          />
        </div>
        <PropertiesPanel template={template} sample={sample} selection={selection} setSelection={setSelection} update={update} />
      </div>
      {preview && <PreviewModal template={template} sample={sample} sourceLabel={describeSource(previewData.meta)} onClose={() => setPreview(false)} />}
    </div>
  );
}
